import React, { useState, useRef, useEffect } from "react";
import { Box, InputLabel, Tooltip } from "@mui/material";
import { EsgPencilIcon, CrossIcon } from "assets";
import { EsgSingleSelectControlledField } from "components/inputs/esg-single-select-field";
import { TextField } from "components/inputs/text-field";
import { COLORS } from "components/consts";

import { CustomDropdownFieldProps } from "./types";
import {
  CUSTOM_OPTION_VALUE,
  CUSTOM_OPTION_LABEL,
  CustomInputContainer,
} from "./consts";

export type { CustomDropdownFieldProps };

export const CustomDropdownField: React.FC<CustomDropdownFieldProps> = ({
  label,
  options,
  selectedValue,
  placeholder,
  onChange,
  disabled = false,
  showCustomOption = true,
  disabledTooltip,
  sxLabel,
  sxSelect,
  sxMenuItem,
  sx,
  required,
  error,
  errorMessage,
}) => {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isCurrentValueCustom =
    !!selectedValue &&
    !options.some((option) => option.value === selectedValue) &&
    selectedValue !== CUSTOM_OPTION_VALUE;

  const hasNoPredefinedOptions = options.length === 0 && !disabled && showCustomOption;

  const customOption = {
    label: CUSTOM_OPTION_LABEL,
    value: CUSTOM_OPTION_VALUE,
    icon: <EsgPencilIcon style={{ width: "14px", height: "14px" }} />,
  };

  const optionsWithCustom = showCustomOption
    ? [...options, customOption]
    : options;

  const handleSelectChange = (value: string | number) => {
    const stringValue = String(value);
    if (stringValue === CUSTOM_OPTION_VALUE) {
      setIsCustomMode(true);
      setCustomValue(
        selectedValue && isCurrentValueCustom ? selectedValue : "",
      );
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setIsCustomMode(false);
      onChange(stringValue);
    }
  };

  // Auto-enter text-input mode when the value isn't in the options list, or when options is empty.
  // disabled controls whether the input is editable.
  useEffect(() => {
    if (isCurrentValueCustom || hasNoPredefinedOptions) {
      setIsCustomMode(true);
      setCustomValue(selectedValue || "");
    } else if (selectedValue !== CUSTOM_OPTION_VALUE) {
      setIsCustomMode(false);
      setCustomValue("");
    }
  }, [options, selectedValue, isCurrentValueCustom, disabled, showCustomOption, hasNoPredefinedOptions]);

  if (isCustomMode) {
    return (
      <Box sx={sx}>
        {label && (
          <InputLabel
            sx={{
              fontSize: "12px",
              color: "black",
              fontWeight: 600,
              mb: "4px",
              ...sxLabel,
            }}
          >
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </InputLabel>
        )}
        <Tooltip title={disabled && disabledTooltip ? disabledTooltip : ''}>
          <CustomInputContainer>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              variant="outlined"
              value={customValue}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(e) => {
                setCustomValue(e.target.value);
                onChange(e.target.value);
              }}
              sx={{
                "& .MuiInputBase-root.MuiOutlinedInput-root": {
                  fontSize: "14px",
                  color: COLORS.Mainblue,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px",
                    borderWidth: "1px",
                    borderColor: COLORS.Mainblue,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.Mainblue,
                    borderWidth: "1px",
                  },
                },
              }}
            />
            {!disabled && (
              <CrossIcon
                style={{
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                  color: "#666",
                  marginBottom: "8px",
                  flexShrink: 0,
                }}
                onClick={() => setIsCustomMode(false)}
              />
            )}
          </CustomInputContainer>
        </Tooltip>
        {error && errorMessage && (
          <Box sx={{ fontSize: "12px", color: "red", mt: "4px" }}>
            {errorMessage}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Tooltip title={disabled && disabledTooltip ? disabledTooltip : ''}>
      <span>
        <EsgSingleSelectControlledField
          sxMenuItem={sxMenuItem}
          sxSelect={sxSelect}
          sx={sx}
          label={label}
          sxLabel={sxLabel}
          options={optionsWithCustom}
          selectedValue={
            isCurrentValueCustom ? CUSTOM_OPTION_VALUE : selectedValue || ""
          }
          setSelectedValue={handleSelectChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          message={error ? errorMessage : undefined}
          color={error ? "error" : undefined}
          width="100%"
        />
      </span>
    </Tooltip>
  );
};
